import { create } from 'zustand'
import type { GameMode } from '@/config/gameModesConfig'
import { useGameStore } from '@/store/gameStore'
import { REGION_META } from '@/config/regionConfig'

interface TutorialStep {
  id: string
  title: string
  message: string
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'
}

interface TutorialState {
  isActive: boolean
  currentStep: number
  steps: TutorialStep[]
  mode: GameMode

  startTutorial: (mode: GameMode) => void
  nextStep: () => void
  skipTutorial: () => void
  reset: () => void
}

// Onboarding is a guided sequence of cards the player advances with a button —
// no dependency on game events (the old version waited for Phaser-era events
// that never fire in the React app, so it got stuck on the "build a court" step).

// Steps are generated per playthrough so the copy can name the player's club
// and zone (region-aware onboarding). `club` and `zona` arrive already
// normalised from startTutorial.
function directorSteps(club: string, zona: string): TutorialStep[] {
  return [
    {
      id: 'welcome',
      title: '¡Bienvenido, director!',
      message: `Acabas de plantar ${club} en ${zona}. Vas a construir y dirigir tu club desde cero, y la junta confía en ti. Te enseño lo esencial en 30 segundos.`,
    },
    {
      id: 'time-money',
      title: 'El tiempo y el dinero',
      message: 'La velocidad del juego (pausa · 1x · 2x · 4x) se ajusta desde Ajustes. (En la versión final será fija y no se podrá cambiar.) Cada día llegan ingresos y gastos: vigila tu saldo, porque quedarte en números rojos demasiado tiempo te cuesta la partida.',
    },
    {
      id: 'facilities',
      title: 'Construye tus instalaciones',
      message: `En Instalaciones compras terreno y levantas pistas y edificios en ${zona}. Las obras tardan unos días: verás su temporizador en la barra superior, y al terminar te avisamos.`,
    },
    {
      id: 'staff',
      title: 'Personal y plantilla',
      message: 'En Personal contratas empleados (recepción, mantenimiento, café…) y fichas jugadores para formar tu plantilla y competir.',
    },
    {
      id: 'compete',
      title: 'Compite y crece',
      message: `En Competición, ${club} juega la liga y los torneos. La junta te marca objetivos de temporada: sigue tu trayectoria en Carrera y haz crecer el club hasta lo más alto.`,
    },
    {
      id: 'done',
      title: '¡A por todas!',
      message: `Ya conoces lo básico. Construye, ficha, gana títulos y haz de ${club} el mejor club de ${zona}. ¡Suerte!`,
    },
  ]
}

function coachSteps(club: string, zona: string): TutorialStep[] {
  return [
    {
      id: 'welcome',
      title: 'Bienvenido al banquillo',
      message: `Te hacen cargo de ${club}, un club ya montado en ${zona}. Olvídate de los ladrillos: tu trabajo es la plantilla y los resultados. La junta espera mucho de ti.`,
    },
    {
      id: 'squad',
      title: 'Tu plantilla manda',
      message: 'En Personal fichas jugadores y tu cuerpo técnico los hace mejorar mes a mes. Una buena plantilla es la diferencia entre ascender y descender.',
    },
    {
      id: 'board',
      title: 'La junta te vigila',
      message: 'Compite en Competición y cumple los objetivos de temporada. Si pierdes la confianza de la junta, te destituyen; si triunfas, llegarán mejores ofertas (en Carrera).',
    },
    {
      id: 'done',
      title: '¡Demuestra tu valía!',
      message: `Tienes club, plantilla y un objetivo. Lo demás depende de ti. ¡Haz grande a ${club}!`,
    },
  ]
}

const doneKey = (mode: GameMode) => `tutorial-${mode}-done`

/** Whether the onboarding for a mode has been seen (or globally skipped). */
export function isTutorialDone(mode: GameMode): boolean {
  if (typeof window === 'undefined') return true
  return localStorage.getItem('tutorial-completed') === 'true' ||
    localStorage.getItem(doneKey(mode)) === 'true'
}

export const useTutorialStore = create<TutorialState>((set, get) => ({
  isActive: false,
  currentStep: 0,
  steps: [],
  mode: 'director',

  startTutorial: (mode) => {
    const g = useGameStore.getState()
    const club = g.managerClubName?.trim() || 'tu club'
    const zona = REGION_META[g.region]?.label ?? 'tu zona'
    set({
      isActive: true,
      currentStep: 0,
      mode,
      steps: mode === 'coach' ? coachSteps(club, zona) : directorSteps(club, zona),
    })
  },

  nextStep: () => {
    const { currentStep, steps, mode } = get()
    if (currentStep < steps.length - 1) {
      set({ currentStep: currentStep + 1 })
    } else {
      localStorage.setItem(doneKey(mode), 'true')
      set({ isActive: false })
    }
  },

  skipTutorial: () => {
    localStorage.setItem(doneKey(get().mode), 'true')
    set({ isActive: false })
  },

  reset: () => {
    localStorage.removeItem('tutorial-completed')
    localStorage.removeItem(doneKey('director'))
    localStorage.removeItem(doneKey('coach'))
    set({ isActive: false, currentStep: 0 })
  },
}))
